import {bindable, customElement, inject} from "aurelia-framework";
import {ObserverLocator} from "aurelia-binding";
import {I18N} from 'aurelia-i18n';

import "./htmlarea.less";

import "tinymce";
import "tinymce/plugins/advlist";
import "tinymce/plugins/anchor";
import "tinymce/plugins/autolink";
import "tinymce/plugins/charmap";
import "tinymce/plugins/code";
import "tinymce/plugins/contextmenu";
import "tinymce/plugins/fullpage";
import "tinymce/plugins/fullscreen";
import "tinymce/plugins/image";
import "tinymce/plugins/insertdatetime";
import "tinymce/plugins/link";
import "tinymce/plugins/lists";
import "tinymce/plugins/media";
import "tinymce/plugins/pagebreak";
import "tinymce/plugins/paste";
import "tinymce/plugins/preview";
import "tinymce/plugins/print";
import "tinymce/plugins/searchreplace";
import "tinymce/plugins/table";
import "tinymce/plugins/visualblocks";
import "tinymce/themes/modern";
import {MediaDatabaseSelect} from './plugin/media-database-select';
import {TextTemplateSelect} from './plugin/text-template-select';
import {ArtificialIntelligence} from "../artificial-intelligence/helper/artificial-intelligence";
import {FlashService} from "../flash/flash-service";
import {Translatable} from "./translatable";
import {ArtificialIntelligencePlugin} from "../artificial-intelligence/plugin/artificial-intelligence-plugin";
import Client from "../api/client";
import {v4 as uuidv4} from 'uuid';

@inject(
    I18N,
    ObserverLocator,
    MediaDatabaseSelect,
    TextTemplateSelect,
    ArtificialIntelligence,
    ArtificialIntelligencePlugin,
    FlashService,
    Translatable,
    Client,
)
@customElement('sio-html-area')
export class HtmlArea {
    @bindable field = null;
    @bindable({defaultBindingMode: 2}) value;

    editorInstance = null;
    editorNode;

    viewChange = false;

    constructor(
        i18n,
        observerLocator,
        mediaDatabaseSelect,
        textTemplateSelect,
        artificialIntelligence,
        artificialIntelligencePlugin,
        flashService,
        translatable,
        client,
    ) {
        this.i18n = i18n;
        this.mediaDatabaseSelect = mediaDatabaseSelect;
        this.textTemplateSelect = textTemplateSelect;
        this.artificialIntelligence = artificialIntelligence;
        this.artificialIntelligencePlugin = artificialIntelligencePlugin;
        this.flash = flashService;
        this.translatable = translatable;
        this.client = client;
    }

    valueChanged(newValue) {
        if (!this.editorInstance) {
            return;
        }

        const currentContent = this.editorInstance.getContent() || '';
        const isValueChanged= (newValue || '') !== currentContent;

        if (this.viewChange && !isValueChanged) {
            this.viewChange = false;
            return;
        }

        this.viewChange = false;

        if (!isValueChanged) {
            return;
        }

        this.editorInstance.setContent(newValue || '');
    }

    async openArtificialIntelligenceDialog(context) {
        const {id, modelId} = context?.parent?.contextObjectRef ?? {};

        try {
            const res = await this.artificialIntelligence.generate({id: id, modelId: modelId});

            if (this.artificialIntelligence.validateResponseObj(res)) {
                return;
            }

            this.value = res.response;
            this.valueChanged();
        } catch (error) {
            console.error(error);
            this.flash.error(error?.localizedMessage ?? error?.message ?? error ?? "Error");
        }
    }

    attached() {
        // TODO: Implement multilangual support
        // Right now TinyMCE crashes with https://github.com/tinymce/tinymce/issues/2655 error when we specify language_url.

        let language = window.localStorage.i18nextLng ?? "de"; //this.i18n.i18next.language;

        this.mediaDatabaseSelect.initialize(tinymce);
        this.textTemplateSelect.initialize(tinymce);
        this.artificialIntelligencePlugin.initialize(tinymce, this.field);

        let config = {
            target: this.editorNode,
            plugins: [
                'advlist autolink lists link image charmap print preview textcolor anchor ' + (this.field.fullPage ? 'fullpage' : ''),
                'searchreplace visualblocks code fullscreen pagebreak mediadatabaseselect texttemplateselect artificialintelligencegenerate',
                'insertdatetime media table contextmenu paste'
            ],
            protect: [
                /{%(.*)%}/g, // Allow TWIG control codes
                /{#(.*)#}/g, // Allow TWIG comment codes
            ],
            image_caption: true,
            browser_spellcheck: true,
            menubar: false,
            height: this.field.options.height ? this.field.options.height : 500,
            toolbar: 'undo redo | insert pastetext | styleselect fontsizeselect forecolor | bold italic | removeformat | alignleft aligncenter alignright alignjustify | bullist numlist outdent indent | link image mediadatabaseselect texttemplateselect artificialintelligencegenerate | code',
            fontsize_formats: '7pt 8pt 9pt 10pt 11pt 12pt 13pt 14pt 15pt 16pt 18pt 20pt 24pt 36pt',
            //content_css: '//www.tinymce.com/css/codepen.min.css',
            skin_url: '/tinymce/skins/lightgray',

            fullpage_default_encoding: 'UTF-8',
            entity_encoding: 'raw',
            extended_valid_elements: 'script[language|type|src]', // allow customers to insert widgets

            language: language,
            language_url: language !== 'en' && '/tinymce/langs/' + language + '.js',

            // reduce size of image to editor size
            content_style: 'img { max-width: 100%; height: auto; }',


            paste_data_images: true,
            automatic_uploads: true,
            images_file_types: 'jpeg,jpg,png,gif,webp',
            images_upload_credentials: false,
            image_uploadtab: true,


            images_upload_handler: async (blobInfo, success, failure, progress) => {
                try {
                    const showProgress = (percentage) => {
                        if (progress) {
                            progress(percentage);
                        }
                    };

                    const blob = blobInfo.blob();
                    const filename = blobInfo.filename() || 'image.png';

                    const formData = new FormData();
                    formData.append('public', 'true');
                    formData.append('keepLocal', 'false');
                    formData.append('storage', '');
                    formData.append('qquuid', uuidv4());
                    formData.append('qqfilename', filename);
                    formData.append('qqtotalfilesize', blob.size);
                    formData.append('qqfile', blob, filename);

                    showProgress(0);


                    const response = await this.client.post('file/file/_uploader/upload/upload', formData);
                    showProgress(100);

                    if (!response.data || !response.data.success || !response.data.uploadedFileId) {
                        failure(this.i18n.tr('sio.form.tinymce.image.upload.failed') + ': ' + JSON.stringify(response.data));
                        return;
                    }

                    const fileInfo = await this.client.get('file/file/' + response.data.uploadedFileId);

                    if (!fileInfo || !fileInfo.previewUrl) {
                        failure(this.i18n.tr('sio.form.tinymce.image.upload.missing-preview-url'));
                        return;
                    }

                    const editorWidth = this.editorInstance ?
                        this.editorInstance.getBody().clientWidth - 40 : 700;

                    const imageUrl = fileInfo.previewUrl
                        .replace('$width', editorWidth.toString())
                        .replace('$height', '');


                    success(imageUrl);

                } catch (error) {
                    console.error('Upload error:', error);

                    if (error.status === 403) {
                        failure(this.i18n.tr('sio.form.tinymce.image.upload.missing-right'));
                    } else {
                        failure(this.i18n.tr('sio.form.tinymce.image.upload.failed') + ': ' + (error.message || JSON.stringify(error)));
                    }
                }
            },

            setup: editor => {

                editor.on('init', e => {
                    this.editorInstance = editor;
                    editor.setContent(this.value || '');
                });
                editor.on('change redo undo keydown', e => {
                    this.viewChange = true;

                    const content = editor.getContent();
                    this.value = '' !== content ? content : null;
                });
                editor.on('UploadSuccess', function() {
                    editor.notificationManager.open({
                        text: 'sio.form.tinymce.image.upload.success',
                        type: 'success',
                        timeout: 2000
                    });
                });
            }
        };

        if (this.field.fullPage) {
            config['force_br_newlines'] = false;
            config['force_p_newlines'] = false;
            config['forced_root_block'] = '';
        }

        tinymce.init(config);
    }
}
